/**
 * CSG shape components for constructive solid geometry.
 *
 * CSGComponent acts as both a shape node and a CSG root. As a root it owns the
 * generated mesh; as a child it contributes its shape via boolean operations.
 * Children are processed in reverse order (last child applied first). When a
 * nested CSGComponent is selected in the editor, its sub-tree result is shown
 * with a transparent color-coded material (green=add, red=subtract, yellow=intersect).
 */
import * as THREE from 'three';

import { getLatest } from '../../utils/Async.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';

import type { Actor } from '../../actors/Actor.js';
import type { ActiveManifold, ManifoldWrapper } from '../../csg/ManifoldWrapper.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { MaterialPath } from '../../utils/serialization/decorator.js';
import type { PrimitiveComponentOptions } from '../PrimitiveComponent.js';
import type { SceneComponent } from '../SceneComponent.js';


export enum CSGOperation {
  Add = 'add',
  Subtract = 'subtract',
  Intersect = 'intersect',
}

export interface CSGComponentOptions extends PrimitiveComponentOptions {
  /** The type of boolean operation to apply when used as a child */
  operation?: CSGOperation;
  /** URL to a material JSON file */
  materialUrl?: string;
  /** Whether the component is enabled */
  enabled?: boolean;
}

/**
 * CSGComponent represents a CSG shape node that can both contribute a shape to
 * a parent CSGComponent and act as a root that processes its own children.
 *
 * As a root it owns the generated `THREE.Mesh` directly (no separate
 * MeshComponent). As a child it exposes `createManifold` so its parent can
 * incorporate its geometry via a boolean operation.
 *
 * Abstract base class — extend this to create concrete shape types.
 */
export abstract class CSGComponent<T extends CSGComponentOptions = CSGComponentOptions> extends PrimitiveComponent {

  private static readonly _defaultMaterial = new THREE.MeshStandardMaterial({ color: 0xcccccc });

  private static readonly _previewMaterials: Record<CSGOperation, THREE.MeshStandardMaterial> = {
    [CSGOperation.Add]: new THREE.MeshStandardMaterial({
      color: 0x44dd66,
      transparent: true,
      opacity: 0.25,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
    [CSGOperation.Subtract]: new THREE.MeshStandardMaterial({
      color: 0xff3333,
      transparent: true,
      opacity: 0.25,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
    [CSGOperation.Intersect]: new THREE.MeshStandardMaterial({
      color: 0xffdd00,
      transparent: true,
      opacity: 0.25,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  };

  /** The mesh owned by this node when it is the CSG root. */
  private _resultMesh: THREE.Mesh | null = null;

  /**
   * The real material(s) saved before a preview overlay is applied,
   * so they can be restored when the component is deselected.
   */
  private _savedMaterial: THREE.Material | THREE.Material[] | null = null;

  /** Set to true just before removal so processCSG skips this component. */
  protected _pendingRemoval: boolean = false;

  @property({ category: 'CSG', description: 'Boolean operation type' })
  public operation: CSGOperation = CSGOperation.Add;

  @property({ category: 'CSG', description: 'Material for the CSG shape' })
  protected materialUrl: MaterialPath | null = null;

  /** Runtime material instance (not serialized) */
  protected material: THREE.Material = CSGComponent._defaultMaterial;

  public override initialize(options?: CSGComponentOptions): void {
    super.initialize(options);
    this.operation = options?.operation ?? this.operation;
    this.enabled = options?.enabled ?? this.enabled;
    if (options?.materialUrl !== undefined) {
      this.materialUrl = options.materialUrl;
      this.loadMaterial(options.materialUrl);
    }
  }

  public override postLoad(): void {
    super.postLoad();
    if (this.materialUrl) {
      this.loadMaterial(this.materialUrl);
    }
  }

  protected async loadMaterial(url: string): Promise<void> {
    const loadedMaterial = await getLatest(
      this,
      'material',
      () => resourceManager.loadGenericMaterial(url)
    );
    if (loadedMaterial) {
      this.material = Array.isArray(loadedMaterial) ? loadedMaterial[0] : loadedMaterial;
      this.triggerCSGUpdate();
    }
  }

  /**
   * Create the manifold shape represented by this component.
   *
   * Override in subclasses to produce concrete geometry. The base
   * implementation returns `null` (no shape), which makes this class usable as
   * a blank combiner root.
   *
   * When `isRoot` is `true` the node's own position and rotation are NOT baked
   * into the Manifold geometry — Three.js already applies them via the scene
   * graph when the result mesh is parented to this node. Child nodes
   * (`isRoot` = `false`) do bake their local transform, since that offset is
   * expressed in the root's coordinate space and must be embedded in the flat
   * Manifold geometry.
   *
   * @param manifoldWrapper - The manifold wrapper instance to use
   * @param isRoot - Whether this node is the CSG root (owns the result mesh)
   * @returns The created ActiveManifold shape or null if creation failed
   */
  public createManifold(_manifoldWrapper: ManifoldWrapper, _isRoot: boolean = false): ActiveManifold | null {
    return null;
  }

  /**
   * Process all direct CSGComponent children and combine them with this
   * component's own shape. The result is stored in a `THREE.Mesh` owned by
   * this node. Children are processed in reverse order so the first child
   * is evaluated last (consistent with how Godot handles CSG trees).
   *
   * Only the CSG root (a CSGComponent whose parent is not itself a
   * CSGComponent) calls this; children contribute via `createManifold`.
   */
  public processCSG(): void {
    const world = this.getWorld();
    if (!world) return;

    const manifoldWrapper = world.getManifold();
    if (!manifoldWrapper) {
      console.error('[CSGComponent] Manifold not available.');
      return;
    }

    // Gather direct CSGComponent children that are still alive
    const csgChildren = this.getComponents(CSGComponent).filter(
      (c) => c !== this && !c._pendingRemoval
    );

    // Build this node's own shape. isRoot=true so position/rotation are NOT
    // baked into the geometry — Three.js applies them via the scene graph.
    let result = this._pendingRemoval ? null : this.createManifold(manifoldWrapper, true);

    if (csgChildren.length === 0 && !result) {
      this._setResultVisible(false);
      return;
    }

    // Process children in reverse order (last child applied first)
    for (let i = csgChildren.length - 1; i >= 0; i--) {
      const child = csgChildren[i];
      const shape = child.createManifold(manifoldWrapper);
      if (!shape) continue;

      if (!result) {
        result = shape;
        continue;
      }

      switch (child.operation) {
        case CSGOperation.Add:
          result = result.add(shape);
          break;
        case CSGOperation.Subtract:
          result = result.subtract(shape);
          break;
        case CSGOperation.Intersect:
          result = result.intersect(shape);
          break;
        default:
          console.warn(`[CSGComponent] Unknown operation: ${child.operation}`);
      }
    }

    if (!result) {
      this._setResultVisible(false);
      return;
    }

    const newMesh = result.toMesh(this.material);

    if (this._resultMesh) {
      // Dispose old geometry and reuse the mesh object
      this._resultMesh.geometry.dispose();
      this._resultMesh.geometry = newMesh.geometry;
      this._resultMesh.material = newMesh.material;
      this._resultMesh.visible = true;
      this.refreshPhysics();
    } else {
      this._resultMesh = newMesh;
      this._resultMesh.setTransient(true);
      this.add(this._resultMesh);
      this.refreshPhysics();
    }
  }

  private _setResultVisible(visible: boolean): void {
    if (this._resultMesh) this._resultMesh.visible = visible;
  }

  public getResultMesh(): THREE.Mesh | null {
    return this._resultMesh;
  }

  public setMaterial(material: THREE.Material): void {
    this.material = material;
    this.materialUrl = null;
  }

  public async setMaterialUrl(url: string): Promise<void> {
    this.materialUrl = url;
    await this.loadMaterial(url);
  }

  public getMaterial(): THREE.Material {
    return this.material;
  }

  public getMaterialUrl(): string | null {
    return this.materialUrl;
  }

  public setOperation(operation: CSGOperation): void {
    this.operation = operation;
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);

    if (path === 'materialUrl') {
      if (value) {
        this.loadMaterial(value).then(() => this.triggerCSGUpdate());
      } else {
        this.material = CSGComponent._defaultMaterial;
        this.triggerCSGUpdate();
      }
      return;
    }

    this.triggerCSGUpdate();
  }

  private triggerCSGUpdate(): void {
    this._findCSGRoot().processCSG();
  }

  private _findCSGRoot(): CSGComponent {

    let node: CSGComponent = this;
    while (node.parent instanceof CSGComponent) {
      node = node.parent;
    }
    return node;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  /**
   * Shows a composite preview of this node's own sub-tree result alongside the
   * normal root mesh. For child nodes, `processCSG()` is run on self to produce
   * a temporary result mesh, then the operation-coded transparent material is
   * applied to it. The parent root mesh is left untouched and remains visible.
   * For the root node itself, the preview material is overlaid on its existing
   * result mesh.
   */
  private showPreview(): void {
    if (this._savedMaterial !== null) return; // already showing

    const isChild = this.parent instanceof CSGComponent;

    if (isChild) {
      // Build our own sub-tree result so we have something to show
      this.processCSG();
    }

    if (!this._resultMesh) return;

    // Swap in the preview material, saving the real one for restore
    this._savedMaterial = this._resultMesh.material;
    this._resultMesh.material = CSGComponent._previewMaterials[this.operation];
    this._resultMesh.visible = true;
  }

  /**
   * Removes the selection preview. Disposes the temporary sub-tree result mesh
   * if this node is a child (the parent root already holds the authoritative
   * result). For the root node, reverts the material swap in place.
   */
  private hidePreview(): void {
    if (this._savedMaterial === null) return;

    const isChild = this.parent instanceof CSGComponent;

    if (this._resultMesh) {
      if (isChild) {
        // Dispose the temporary sub-tree mesh
        this._resultMesh.geometry.dispose();
        this._resultMesh.removeFromParent();
        this._resultMesh = null;
      } else {
        // Restore the real material on the root mesh
        this._resultMesh.material = this._savedMaterial;
      }
    }

    this._savedMaterial = null;
  }

  public override beginPlay(): void {
    super.beginPlay();
    // Only the root processes CSG on beginPlay
    if (!(this.parent instanceof CSGComponent)) {
      this.processCSG();
    }
  }

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    this.triggerCSGUpdate();
  }

  public override onEditorRemoveFromWorld(): void {
    super.onEditorRemoveFromWorld();
    this.hidePreview();
    this._pendingRemoval = true;
    this.triggerCSGUpdate();
  }

  public override onEditorSelectedChanged(isSelected: boolean, selectedActors: Actor[]): void {
    super.onEditorSelectedChanged(isSelected, selectedActors);
    if (!isSelected) {
      this.hidePreview();
    }
    // Preview for child components is now driven by onEditorComponentSelectionChanged
  }

  public override onEditorComponentSelectionChanged(selectedComponent: SceneComponent | null, selectedActors: Actor[]): void {
    super.onEditorComponentSelectionChanged(selectedComponent, selectedActors);
    const isChild = this.parent instanceof CSGComponent;
    if (!isChild) return; // Root always displays normally — no preview overlay

    if (selectedComponent === this) {
      this.showPreview();
    } else {
      this.hidePreview();
    }
  }

  public override getConfigurationWarnings(): string[] {
    return super.getConfigurationWarnings();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_CSG';
  }
}
